<template>
  <div>
    <div v-if="filters.length > 1" class="flex center py-8 fw-light <md:hidden" :class="{ wrap: isViewMore }">
      <base-button
        class="ws-nowrap b-r b-$theme px-10 py-2 hover:underlined duration"
        :class="{ underlined: !activeFilter }"
        @click="activeFilter = ''"
      >
        {{ $t.viewAllEvents }}
      </base-button>
      <base-button
        v-for="(filter, key) in truncatedFilters"
        :key
        class="max-w-1/2 truncate px-10 py-2 text-sm hover:underlined duration"
        :class="{ 'b-r b-$theme': key < truncatedFilters.length - 1, 'underlined': filter === activeFilter }"
        @click="activeFilter = filter"
      >
        {{ filter }}
      </base-button>
      <base-button
        v-if="filters.length > 3"
        class="ws-nowrap b-l b-$theme px-10 py-2 underlined"
        @click="isViewMore = !isViewMore"
      >
        <span>{{ isViewMore ? $t.viewLess : $t.viewMore }}</span>
      </base-button>
    </div>
    <div v-if="filters.length > 1" class="my-4 md:hidden">
      <vf-select v-model="activeFilter" :options="filters" :placeholder="$t.viewAllEvents">
        {{ $t.events }}
      </vf-select>
    </div>
    <div class="overflow-hidden">
      <base-grid class="md:-mx-4" :cols="{ sm: 1, md: 2, lg: 3 }">
        <transition-group name="slide">
          <div v-for="(event, key) in filteredEvents" :key class="slide-up b-b b-$theme">
            <base-lazy-hydrate when="interaction">
              <vf-event-card
                :date="event.date"
                :image="event.image"
                :label="event.location?.countryCode"
                :title="event.title"
                @show-event="open(event)"
              />
            </base-lazy-hydrate>
          </div>
        </transition-group>
      </base-grid>
    </div>
    <transition name="nested">
      <vf-modal v-if="activeEvent" class-content="!p-0" size="full" @resolve="close">
        <base-dialog-close
          :aria-label="$t.close"
          class="absolute right-0 top-0"
          data-test-id="vf-modal-close"
        >
          <div class="relative p-4 hover:c-grey-20">
            <div class="absolute-0 bg-white op-50 duration hover:op-100" />
            <vf-icon class="pointer-events-none" name="close" size="lg" />
          </div>
        </base-dialog-close>

        <div class="h-screen">
          <div class="h-1/2 flex">
            <base-picture class="grow" v-bind="activeEvent!.mediaList[0]" fit="cover" />
            <base-picture class="w-1/3 shrink-0 <lg:hidden" v-bind="activeEvent!.mediaList[1]" fit="cover" />
          </div>
          <div class="min-h-1/2 flex">
            <div class="f-col grow gap-8 px-4 pt-8 lg:flex-row lg:p-8 lg:pt-16">
              <div class="f-col between lg:w-1/3">
                <div>
                  <h1 class="<lg:text-center title-1">
                    {{ activeEvent.title }}
                  </h1>
                  <div class="mt-4 flex gap-6 <lg:center">
                    <base-link v-for="(to, name) in shareLinks" :key="name" class="duration hover:c-grey-30" :to>
                      <vf-icon :name size="lg" />
                    </base-link>
                  </div>
                </div>
                <p class="mt-6 <lg:text-center">
                  {{ activeEvent.location.completeName }} | {{ eventDate }}
                </p>
              </div>
              <div class="f-col grow lg:w-2/3 <lg:text-center">
                <div v-html="activeEvent.bodyCopy.html" class="mb-6 fw-light <lg:mt-6 lg:mb-4" />
                <template v-if="activeEvent.bookingUrl">
                  <template v-if="enableLoyaltyFeatures && !user.isLoyaltyMember">
                    <p class="fw-medium">
                      {{ $t.event.reservedForMembers }}
                    </p>
                    <div class="mt-6 w-max f-col gap-2 lg:mt-4 <lg:self-center">
                      <vf-button class="uppercase" @click="DialogLoyaltySignUp.open({ formLocation: 'modal:single:pop-up:none' })">
                        {{ $t.event.join }}
                      </vf-button>
                      <vf-button class="uppercase" @click="DialogSignIn.open({ formLocation: 'modal:single:pop-up:none' })">
                        {{ $t.event.alreadyMember }}
                      </vf-button>
                    </div>
                  </template>
                  <template v-else>
                    <vf-button class="mt-6 w-max self-center uppercase lg:mt-4 lg:self-start" :to="activeEvent.bookingUrl">
                      {{ $t.event.reserveSpot }}
                    </vf-button>
                    <p v-if="user.isLoyaltyMember" class="mt-6 fw-medium lg:mt-4">
                      {{ $t.event.congrats }}
                    </p>
                  </template>
                </template>
                <template v-else>
                  <p class="fw-medium">
                    {{ $t.event.registrationClosed }}
                  </p>
                  <template v-if="enableLoyaltyFeatures">
                    <template v-if="user.isLoyaltyMember">
                      <p class="mt-6 fw-light lg:mt-4">
                        {{ $t.event.firstToKnow }}
                      </p>
                      <p class="fw-light">
                        {{ $t.event.stayTuned }}
                      </p>
                    </template>
                    <template v-else>
                      <p class="mt-6 fw-light lg:mt-4">
                        {{ $t.event.joinToGetNews }}
                      </p>
                      <p class="fw-light">
                        {{ $t.event.stayTuned }}
                      </p>
                      <vf-button class="mt-6 w-max self-center uppercase lg:mt-4 lg:self-start" @click="DialogLoyaltySignUp.open({ formLocation: 'modal:single:pop-up:none' })">
                        {{ $t.joinLoyaltyCta }}
                      </vf-button>
                      <p class="mt-6 fw-light lg:mt-4">
                        {{ $t.event.noWorries }}
                      </p>
                    </template>
                  </template>
                  <template v-else>
                    <p class="mt-6 fw-light lg:mt-4">
                      {{ $t.event.subscribe }}
                    </p>
                    <vf-button class="mt-6 w-max self-center uppercase lg:mt-4 lg:self-start" to="/newsletter-sign-up">
                      {{ $t.event.stayTuned }}
                    </vf-button>
                  </template>
                </template>
                <div class="mt-4 f-col grow items-center gap-4 px-8 py-6 -mx-4 lg:flex-row lg:items-start <lg:bg-grey-90">
                  <div class="mb-4 w-1/2">
                    <cms-rich-text :content="{ richText: activeEvent.leftCopy }" />
                  </div>
                  <div class="mb-4 w-1/2">
                    <cms-rich-text :content="{ richText: activeEvent.rightCopy }" />
                  </div>
                </div>
              </div>
            </div>
            <div class="relative w-1/3 shrink-0 <lg:hidden">
              <base-picture class="absolute-0 h-full" v-bind="activeEvent!.mediaList[2]" fit="cover" />
            </div>
          </div>
        </div>
      </vf-modal>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import type { EventCollectionContent } from '#types/components/cms/event-collection'
import type { EventCollectionItem } from '#types/components/cms/event-collection-item'

const props = defineProps<{ content: EventCollectionContent }>()
const { items = [] } = props.content

const { $t } = useNuxtApp()
const user = useUserStore()
const route = useRoute()
const origin = useOrigin()
const eventQuery = useRouteQuery('event')
const { DialogSignIn, DialogLoyaltySignUp } = useDialogsStore()
const { enableLoyaltyFeatures } = useFeatureFlags()

const isViewMore = ref(false)
const activeEvent = ref<EventCollectionItem>()
const activeFilter = ref('')

// Due to CoreMedia limitations, we need to transform the data on the client
// to better match used component props contract
const events: EventCollectionItem[] = items.map((item) => ({
  ...item as any,
  date: {
    start: item.startDate,
    end: item.endDate
  },
  image: {
    ...item.image,
    src: item.image.url
  },
  mediaList: item.mediaList.map(({ sm, md, lg }) => ({
    src: {
      sm: sm?.url,
      md: md?.url,
      lg: lg?.url,
    },
    width: {
      sm: sm?.width,
      md: md?.width,
      lg: lg?.width,
    },
    height: {
      sm: sm?.height,
      md: md?.height,
      lg: lg?.height,
    },
    alt: sm?.alt
  })),
}))

// Open event preview if query param is present
if (eventQuery.value) {
  const event = events.find(({ id }) => id === eventQuery.value)
  if (event) activeEvent.value = event
}

const filters = [...new Set(events.map(({ location }) => location?.name))]

const truncatedFilters = computed(() => isViewMore.value ? filters : [...filters].splice(0, 3))

const filteredEvents = computed(() =>
  events.filter(({ location }) => activeFilter.value ? location.name === activeFilter.value : true)
)

const eventDate = computed(() => {
  if (!activeEvent.value) return

  const eventStartDate = activeEvent.value.startDate
  if (!eventStartDate) return $t.comingSoon

  const startDate = new Date(eventStartDate).toLocaleDateString()

  if (activeEvent.value.endDate)
    return `${startDate} - ${new Date(activeEvent.value.endDate).toLocaleDateString()}`

  return startDate
})

const eventUrl = computed(() => origin + route.fullPath)

const shareLinks = reactive({
  facebook: `https://www.facebook.com/dialog/feed?app_id=231988623523365&display=popup&link=${eventUrl.value}`,
  x: `https://twitter.com/intent/tweet?text=${activeEvent.value?.title}&url=${eventUrl.value}`,
  mail: `mailto:?subject=${activeEvent.value?.title}&body=${eventUrl.value}`
})

const open = (e: EventCollectionItem) => {
  activeEvent.value = e
  eventQuery.value = e.id
}

const close = () => {
  activeEvent.value = undefined
  eventQuery.value = null
  document.body.style.removeProperty('overflow')
}
</script>
